import { ClickAwayListener } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import { Translate, useTranslation } from "@redotech/redo-i18n";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  filterGroupsAreEqual,
  isFilterGroupValid,
} from "@redotech/redo-views-common/utils/util";
import { deepCopyObject } from "@redotech/util/object";
import Fuse from "fuse.js";
import { memo, useMemo, useRef, useState } from "react";
import { BaseRedoInput } from "../../arbiter-components/input/base-redo-text-input";
import { RedoModal } from "../../arbiter-components/modal/redo-modal";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { Tooltip } from "../../tooltip/tooltip";
import { AdvancedTableFilter } from "../advanced-filter";
import { addFilterToGroup, FilterGroupBuilder } from "./filter-group-builder";
import * as styles from "./filter-group-chip.module.css";

export interface FilterGroupChipProps {
  filterGroup: FilterGroup;
  onFilterGroupChange: (filterGroup: FilterGroup) => void;
  onRemove: () => void;
  availableFilters: AdvancedTableFilter[];
  renderFilter: (props: {
    filter: AdvancedTableFilter;
    onFilterChange: (filter: AdvancedTableFilter) => void;
    onRemove: () => void;
    allFilters: AdvancedTableFilter[];
  }) => React.ReactNode;
}

function countRules(group: FilterGroup): number {
  let count = 0;
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      count += countRules(clause);
    } else {
      count += 1;
    }
  }
  return count;
}

function getChipLabel(t: Translate, group: FilterGroup): string {
  const ruleCount = countRules(group);
  if (ruleCount === 0) {
    return t("Empty filter group", {
      context: "label for a filter group that has no filters yet",
    });
  }
  return t("{{count}} filters", { count: ruleCount });
}

function getTooltipContent(
  t: Translate,
  group: FilterGroup,
  availableFilters: AdvancedTableFilter[],
): string {
  const operator =
    group.operator === FilterOperator.AND
      ? t("and", { context: "filter group operator" })
      : t("or", { context: "filter group operator" });

  const parts: string[] = [];
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      const nested = getTooltipContent(t, clause, availableFilters);
      parts.push(`(${nested})`);
    } else {
      const filter = availableFilters.find((f) => f.data.name === clause.name);
      const displayName = filter?.displayName ?? clause.name;
      parts.push(displayName);
    }
  }

  return parts.join(` ${operator} `);
}

export const FilterGroupChip = memo(function FilterGroupChip({
  filterGroup,
  onFilterGroupChange,
  onRemove,
  availableFilters,
  renderFilter,
}: FilterGroupChipProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<FilterGroup>(filterGroup);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [filterSearchText, setFilterSearchText] = useState("");
  const [addFilterButtonRef, setAddFilterButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const filterSearcher = useMemo(() => {
    return new Fuse<AdvancedTableFilter>([], {
      keys: ["displayName"],
      threshold: 0.3,
    });
  }, []);

  const searchedFilters = useSearch(
    filterSearcher,
    availableFilters,
    filterSearchText,
  );

  const handleOpenModal = useHandler(() => {
    setEditingGroup(filterGroup);
    setIsModalOpen(true);
  });

  const handleCloseModal = useHandler(() => {
    setIsModalOpen(false);
    setIsFilterDropdownOpen(false);
    setFilterSearchText("");
  });

  const handleSave = useHandler(() => {
    onFilterGroupChange(editingGroup);
    setIsModalOpen(false);
  });

  const handleEditingGroupChange = useHandler((newGroup: FilterGroup) => {
    setEditingGroup(newGroup);
  });

  // Store the callback to invoke when a filter is selected
  const pendingFilterCallback = useRef<
    ((filter: AdvancedFilterData) => void) | null
  >(null);

  const handleAddFilterClick = useHandler(
    (onFilterSelected?: (filter: AdvancedFilterData) => void) => {
      pendingFilterCallback.current = onFilterSelected ?? null;
      setIsFilterDropdownOpen(true);
    },
  );

  const handleSelectFilter = useHandler((filter: AdvancedTableFilter) => {
    const newFilter = deepCopyObject(filter);

    // If there's a pending callback (from nested group), call it
    // Otherwise add to the root editing group
    if (pendingFilterCallback.current) {
      pendingFilterCallback.current(newFilter.data);
      pendingFilterCallback.current = null;
    } else {
      const newGroup = addFilterToGroup(editingGroup, newFilter.data);
      setEditingGroup(newGroup);
    }

    setIsFilterDropdownOpen(false);
    setFilterSearchText("");
  });

  const label = getChipLabel(t, filterGroup);

  const hasChanges = useMemo(() => {
    return !filterGroupsAreEqual(filterGroup, editingGroup);
  }, [filterGroup, editingGroup]);

  const tooltipContent = useMemo(() => {
    if (filterGroup.clauses.length === 0) {
      return t("Empty filter group", {
        context: "label for a filter group that has no filters yet",
      });
    }
    return getTooltipContent(t, filterGroup, availableFilters);
  }, [filterGroup, availableFilters, t]);

  return (
    <>
      <Tooltip leftAlign placement="bottom" title={tooltipContent}>
        <div className={styles.chip} onClick={handleOpenModal}>
          <Flex align="center" gap="xs">
            <FilterLinesIcon className={styles.icon} />
            <Text fontSize="sm" fontWeight="medium">
              {label}
            </Text>
          </Flex>
          <span className={styles.separator} />
          <button
            className={styles.removeButton}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <XCloseIcon className={styles.removeIcon} />
          </button>
        </div>
      </Tooltip>

      <RedoModal
        footerBorder
        headerBorder
        isOpen={isModalOpen}
        onModalCloseRequested={handleCloseModal}
        primaryButton={{
          text: t("Confirm"),
          onClick: handleSave,
          disabled: !hasChanges || !isFilterGroupValid(editingGroup),
          size: "xs",
        }}
        secondaryButton={{
          text: t("Cancel"),
          onClick: handleCloseModal,
          size: "xs",
        }}
        title={t("Edit filter group")}
      >
        <Flex dir="column" gap="md" px="lg" py="lg">
          <FilterGroupBuilder
            addFilterButtonRef={setAddFilterButtonRef}
            availableFilters={availableFilters}
            filterGroup={editingGroup}
            onAddFilterClick={handleAddFilterClick}
            onFilterGroupChange={handleEditingGroupChange}
            renderFilter={renderFilter}
          />
        </Flex>

        {/* Filter dropdown for adding filters to this group */}
        {isFilterDropdownOpen && (
          <ClickAwayListener
            onClickAway={() => {
              setIsFilterDropdownOpen(false);
              setFilterSearchText("");
              pendingFilterCallback.current = null;
            }}
          >
            <Dropdown
              anchor={addFilterButtonRef}
              className={styles.filterDropdown}
              fitToAnchor={false}
              flexProps={{ p: "none" }}
              open={isFilterDropdownOpen}
            >
              <Flex flexDirection="column" gap="none">
                <Flex
                  borderBottomWidth="1px"
                  borderColor="primary"
                  borderStyle="solid"
                  flexDirection="column"
                  px="lg"
                  py="md"
                >
                  <BaseRedoInput
                    autoFocus
                    placeholder={t("Search filters...")}
                    setValue={setFilterSearchText}
                    size="sm"
                    value={filterSearchText}
                  />
                </Flex>
                <Flex flexDirection="column" px="lg" py="md">
                  {searchedFilters.map((filter) => (
                    <Flex
                      className={styles.filterItem}
                      key={filter.data.name}
                      onClick={() => handleSelectFilter(filter)}
                    >
                      <filter.Icon />
                      <Text fontSize="sm">{filter.displayName}</Text>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            </Dropdown>
          </ClickAwayListener>
        )}
      </RedoModal>
    </>
  );
});
